import { useCallback } from 'react';

import { hasNativeShare } from 'src/shared/helpers/environment';
import {
  TranslationNamespace,
  useImageNativeShareHandler,
  useTranslate,
} from 'src/shared/hooks';

import { HomeownerAppMenuNames } from '../providers/HomeownerAppProvider';
import type { AiOrSampleGeneratedImage } from '../types';
import { isSampleGenerated } from '../types';

import { useCopyShareHandler, useShareToast } from './useCopyShareHandler';
import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseShareGeneratedImageOptions {
  image: AiOrSampleGeneratedImage;
}

export const useShareGeneratedImage = ({
  image,
}: UseShareGeneratedImageOptions) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { setCurrentMenu } = useHomeownerAppContext();

  const toast = useShareToast();
  const handleCopyShare = useCopyShareHandler();
  const onShareFail = useCallback(
    () =>
      toast({
        description: t('share.messages.fail'),
        status: 'error',
      }),
    [toast, t],
  );

  const isSampleImage = isSampleGenerated(image);

  const handleNativeShare = useImageNativeShareHandler({
    imageUrl: isSampleImage ? '' : image.generatedImagePath,
    text: t('share.shareOnSocial'),
    title: isSampleImage ? undefined : image.style,
    onShareFail,
  });

  return useCallback(() => {
    if (isSampleImage) {
      setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
      return;
    }

    if (hasNativeShare()) {
      handleNativeShare();
    } else {
      void handleCopyShare(image.generatedImagePath);
    }
  }, [
    handleCopyShare,
    handleNativeShare,
    image.generatedImagePath,
    isSampleImage,
    setCurrentMenu,
  ]);
};
